import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import Modal from './Modal';
import { FileUpload } from './ImageUpload';
import { fmtDate as fmtDateUtil } from '../timefmt';

// Shared archive of legacy / imported datasheets. Same data the Migrate tab
// searches. SharePoint folder sync is gated until Microsoft Graph creds exist.
export default function ArchiveView({ toast, canManage }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [spSync, setSpSync] = useState(false);

  const load = useCallback(async () => {
    try { const { items } = await api.listLegacy(q); setItems(items || []); }
    catch (e) { console.error('Failed to load archive', e); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  async function onUpload(files) {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const f of files) await api.uploadLegacy(f, (f.name || '').replace(/\.[^.]+$/, ''), 'upload');
      toast(files.length + ' file(s) uploaded to the archive');
      load();
    } catch (e) { toast('Upload failed: ' + (e.message || e), true); }
    setBusy(false);
  }
  async function download(it) {
    try {
      const url = await api.getLegacyObjectUrl(it.id);
      const a = document.createElement('a');
      a.href = url; a.download = (it.name || 'datasheet') + (/\.pdf$/i.test(it.name || '') ? '' : '');
      document.body.appendChild(a); a.click(); a.remove();
      toast('Downloading…');
    } catch (e) { toast('Download failed', true); }
  }
  function preview(it) { api.getLegacyObjectUrl(it.id).then(url => window.open(url, '_blank')).catch(() => toast('Could not open', true)); }
  async function syncSharePoint() {
    setSpSync(true);
    try {
      const r = await api.spSyncArchive();
      if (r.imported) toast(`Synced ${r.imported} new file${r.imported > 1 ? 's' : ''} from SharePoint` + (r.skipped ? ` (${r.skipped} already here)` : ''));
      else toast(r.total ? 'Archive is already up to date with SharePoint' : 'The SharePoint folder is empty');
      load();
    } catch (e) {
      const st = e.response?.status;
      toast(st === 503 ? 'SharePoint is not connected yet.' : 'SharePoint sync failed: ' + (e.response?.data?.detail || e.message), true);
    }
    setSpSync(false);
  }
  async function doDelete() { const it = confirmDel; setConfirmDel(null); await api.deleteLegacy(it.id); load(); toast('Removed from archive'); }

  const fmtSize = (b) => (b ? (b > 1e6 ? (b / 1e6).toFixed(1) + ' MB' : Math.round(b / 1e3) + ' KB') : '');
  const fmtDate = (d) => fmtDateUtil(d);

  const nameKey = (it) => (it.name || '').trim().toLowerCase();
  const nameCounts = {};
  items.forEach(it => { const k = nameKey(it); if (k) nameCounts[k] = (nameCounts[k] || 0) + 1; });
  const dupCount = items.filter(it => nameCounts[nameKey(it)] > 1).length;

  return (
    <div className="archive-view" data-testid="archive-view">
      <div className="archive-head">
        <div>
          <h2>Old Datasheets Archive</h2>
          <p className="codegen-sub">Every imported / legacy datasheet lives here — searchable, downloadable, and shared with the Migrate tab. These never enter the Core Range or Customer libraries.</p>
        </div>
        <div className="archive-actions">
          {canManage && <FileUpload className="btn btn-primary" testid="archive-upload" label={busy ? 'Uploading…' : 'Upload'} disabled={busy} multiple accept="image/*,application/pdf" onFiles={onUpload} />}
          {canManage && <button className="btn" data-testid="archive-sharepoint" disabled={spSync} onClick={syncSharePoint} title="Pull PDFs from the SharePoint datasheets folder into this archive">{spSync ? 'Syncing…' : 'Sync SharePoint'}</button>}
        </div>
      </div>

      <input className="input archive-search" data-testid="archive-search" placeholder="Search the archive by name…" value={q} onChange={e => setQ(e.target.value)} />
      {dupCount > 0 && (
        <div className="ar-dup-note" data-testid="archive-dup-note">
          {dupCount} file{dupCount > 1 ? 's' : ''} share a name with another file — flagged below.
        </div>
      )}

      <div className="archive-list">
        {items.length === 0 ? <div className="empty-note">{q ? 'No matches.' : 'The archive is empty. Upload old datasheets to get started.'}</div> :
          items.map(it => (
            <div className="archive-row" key={it.id} data-testid={'archive-item-' + it.id}>
              <span className="ar-icon">{/\.pdf$/i.test(it.name || '') || (it.content_type || '').includes('pdf') ? 'PDF' : 'IMG'}</span>
              <div className="ar-main">
                <div className="ar-name">{it.name || '(Untitled)'}
                  {nameCounts[nameKey(it)] > 1 && <span className="ar-dup" data-testid={'archive-dup-' + it.id}>Duplicate name</span>}
                </div>
                <div className="ar-meta">{[it.source, fmtSize(it.size), fmtDate(it.created_at)].filter(Boolean).join(' · ')}</div>
              </div>
              <button className="btn btn-sm" data-testid={'archive-view-' + it.id} onClick={() => preview(it)}>View</button>
              <button className="btn btn-sm" data-testid={'archive-download-' + it.id} onClick={() => download(it)}>Download</button>
              {canManage && <button className="btn btn-sm btn-danger" onClick={() => setConfirmDel(it)} title="Remove">✕</button>}
            </div>
          ))}
      </div>

      <Modal open={!!confirmDel} title="Remove from archive?" onClose={() => setConfirmDel(null)} testid="archive-delete-modal"
        actions={<>
          <button className="btn" onClick={() => setConfirmDel(null)}>Cancel</button>
          <button className="btn btn-danger-solid" data-testid="archive-delete-btn" onClick={doDelete}>Remove</button>
        </>}>
        <p>Permanently remove <strong>{confirmDel && (confirmDel.name || 'this file')}</strong> from the archive?</p>
      </Modal>
    </div>
  );
}
